import { productCatalogue } from '../../catalogue/products';
import { productInfo } from '../../lib/productInfo';
import { relativeTimeFrom } from '../../lib/relativeTime';
import type { AdminAccessRequest } from '../../api/accessRequests';
import type { components } from '../../api/generated/entitlements';
import { groupRoles, roleLevel, type RoleOption } from './components/roleEditorLogic';

type ApiUser = components['schemas']['UserView'];

export type RequestRow = {
  id: string;
  membershipId: string;
  productId: string;
  productName: string;
  logo: string | null;
  requestedAt: string;
  name: string;
  email: string;
  currentRoles: string[];
};

export type ProductSummary = {
  productId: string;
  productName: string;
  logo: string | null;
  count: number;
  oldestRequestedAt: string;
};

export const PAGE_SIZE = 25;

export function buildRows(requests: readonly AdminAccessRequest[], users: readonly ApiUser[]): RequestRow[] {
  const byMembership = new Map(users.map((u) => [u.id, u]));
  return [...requests]
    .sort((a, b) => a.requestedAt.localeCompare(b.requestedAt))
    .map((r) => {
      const user = byMembership.get(r.membershipId);
      return {
        id: r.id,
        membershipId: r.membershipId,
        productId: r.productId,
        ...productInfo(r.productId),
        requestedAt: r.requestedAt,
        name: user?.name ?? user?.email ?? 'Unknown member',
        email: user?.email ?? '',
        currentRoles: r.roles,
      };
    });
}

export function summariseByProduct(rows: readonly RequestRow[]): ProductSummary[] {
  const byProduct = new Map<string, ProductSummary>();
  for (const row of rows) {
    const current = byProduct.get(row.productId);
    if (!current) {
      byProduct.set(row.productId, {
        productId: row.productId, productName: row.productName, logo: row.logo, count: 1, oldestRequestedAt: row.requestedAt,
      });
    } else {
      current.count += 1;
      if (row.requestedAt < current.oldestRequestedAt) current.oldestRequestedAt = row.requestedAt;
    }
  }
  const order = (id: string) => {
    const i = productCatalogue.findIndex((p) => p.key === id);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...byProduct.values()].sort((a, b) => order(a.productId) - order(b.productId));
}

export function filterRows(rows: readonly RequestRow[], search: string, productId: string): RequestRow[] {
  const q = search.trim().toLowerCase();
  return rows.filter((r) =>
    (productId === 'all' || r.productId === productId)
    && (!q || r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)));
}

export function pageOf<T>(items: readonly T[], page: number, size = PAGE_SIZE): T[] {
  const start = (page - 1) * size;
  return items.slice(start, start + size);
}

export function pageCount(total: number, size = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / size));
}

export function rolesForProduct(available: readonly RoleOption[], productId: string): RoleOption[] {
  return groupRoles([...available]).products.find((g) => g.key === productId)?.roles ?? [];
}

export function defaultRoleFor(productId: string, roles: readonly RoleOption[]): string | null {
  const preferred = productCatalogue.find((p) => p.key === productId)?.defaultRequestRole;
  if (preferred && roles.some((r) => r.slug === preferred)) return preferred;
  return roles[0]?.slug ?? null;
}

export function roleLabel(productId: string, slug: string): string {
  const level = roleLevel(productId, slug);
  return level.charAt(0).toUpperCase() + level.slice(1);
}

export function waitingLine(count: number, oldestRequestedAt: string, now: Date = new Date()): string {
  return `${count} ${count === 1 ? 'person' : 'people'} waiting · oldest ${relativeTimeFrom(oldestRequestedAt, now)}`;
}
